import React from 'react';
import { Typography, Card, Space, Button } from 'antd';
import { generateTemplates } from './ApplyByTextTemplates';
import ColoredQRCode from './ColoredQRCode';
import scanToApplyIcon from '../../assets/qr/scantoapply.png';

const { Title, Text } = Typography;
const LETTER_SIZE = { w: 816, h: 1056 };
const SAFE_MARGIN = 96; // ~1 inch at 96dpi
const SAFE_AREA = {
  x: SAFE_MARGIN,
  y: SAFE_MARGIN,
  w: LETTER_SIZE.w - (SAFE_MARGIN * 2),
  h: LETTER_SIZE.h - (SAFE_MARGIN * 2)
};
const THUMB_SIZE = { w: 118, h: 150 };
const PAGE_SCALE = Math.min(
  THUMB_SIZE.w / LETTER_SIZE.w,
  THUMB_SIZE.h / LETTER_SIZE.h
);

const fitElementsToSafeArea = (elements = []) => {
  if (!elements.length) {
    return [];
  }

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  elements.forEach(({ layout }) => {
    minX = Math.min(minX, layout.x);
    minY = Math.min(minY, layout.y);
    maxX = Math.max(maxX, layout.x + layout.w);
    maxY = Math.max(maxY, layout.y + layout.h);
  });

  const contentW = Math.max(1, maxX - minX);
  const contentH = Math.max(1, maxY - minY);
  const fitScale = Math.min(SAFE_AREA.w / contentW, SAFE_AREA.h / contentH);

  const offsetX = SAFE_AREA.x + ((SAFE_AREA.w - (contentW * fitScale)) / 2) - (minX * fitScale);
  const offsetY = SAFE_AREA.y + ((SAFE_AREA.h - (contentH * fitScale)) / 2) - (minY * fitScale);

  return elements.map((element) => ({
    ...element,
    layout: {
      ...element.layout,
      x: (element.layout.x * fitScale) + offsetX,
      y: (element.layout.y * fitScale) + offsetY,
      w: element.layout.w * fitScale,
      h: element.layout.h * fitScale
    }
  }));
};

const renderRichNode = (node, key) => {
  if (node.type === 'hardBreak') {
    return <br key={key} />;
  }

  if (node.type !== 'text') {
    return null;
  }

  const style = {};
  (node.marks || []).forEach((mark) => {
    if (mark.type === 'bold') {
      style.fontWeight = 700;
    }
    if (mark.type === 'textStyle' && mark.attrs && mark.attrs.color) {
      style.color = mark.attrs.color;
    }
  });

  return <span key={key} style={style}>{node.text}</span>;
};

const TemplateThumbnail = ({ template, keyword, defaultABT }) => {
  const elements = fitElementsToSafeArea(template.elements);
  const isCorporateModern = template.id === 'template-corporate-modern-02';

  const renderElement = (element) => {
    const commonStyle = {
      position: 'absolute',
      left: element.layout.x,
      top: element.layout.y,
      width: element.layout.w,
      height: element.layout.h,
      overflow: 'hidden'
    };

    if (element.type === 'logo') {
      return (
        <div key={element.id} style={{ ...commonStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {element.props?.url ? (
            <img
              src={element.props.url}
              alt="Logo"
              style={{ width: '100%', height: '100%', objectFit: element.props?.fitMode || 'contain' }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', border: '1px dashed #cbd5e1', background: '#f8fafc' }} />
          )}
        </div>
      );
    }

    if (element.type === 'qr') {
      const qrSize = Math.max(12, Math.min(element.layout.w, element.layout.h) * 0.88);
      const iconSizeRatio = element.props?.iconSizeRatio || 0.2;
      const containerStyleProps = element.props?.containerStyle || {};
      
      const borderSize = containerStyleProps.border ? parseInt(containerStyleProps.border, 10) : 0;
      const paddingSize = containerStyleProps.padding ? parseInt(containerStyleProps.padding, 10) : 0;
      const labelHeight = element.props?.showLabel ? 32 : 0;
      const innerSize = Math.max(10, qrSize - (borderSize * 2) - (paddingSize * 2) - labelHeight);

      return (
        <div
          key={element.id}
          style={{
            ...commonStyle,
            ...containerStyleProps,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          <ColoredQRCode
            value={`sms:${defaultABT}?body=${keyword}`}
            size={innerSize}
            errorLevel="H"
            color={element.props?.color || '#000000'}
            bgColor={element.props?.bgColor || 'transparent'}
            finderColor={element.props?.finderColor || '#38b549'}
            icon={element.props?.showIcon === false ? undefined : scanToApplyIcon}
            iconSize={Math.max(6, innerSize * iconSizeRatio)}
          />
        </div>
      );
    }

    if (element.type === 'text') {
      const block = element.content?.content?.find((item) => item.type === 'heading' || item.type === 'paragraph');
      if (!block) {
        return null;
      }

      const level = block.attrs?.level || 3;
      const isHeading = block.type === 'heading';
      const headingSizes = isCorporateModern ? { 1: 58, 2: 38, 3: 25 } : { 1: 54, 2: 38, 3: 25 };
      const fontSize = isHeading ? (headingSizes[level] || 22) : 21;
      const fontWeight = isHeading ? ({ 1: 800, 2: 700, 3: 600 }[level] || 600) : 400;
      const textContainerStyleProps = element.props?.containerStyle || {};

      return (
        <div
          key={element.id}
          style={{
            ...commonStyle,
            textAlign: block.attrs?.textAlign || 'left',
            lineHeight: isHeading ? 1.12 : 1.22,
            fontSize,
            fontWeight,
            color: '#111827',
            boxSizing: 'border-box',
            ...textContainerStyleProps
          }}
        >
          {(block.content || []).map((node, index) => renderRichNode(node, `${element.id}-${index}`))}
        </div>
      );
    }

    return null;
  };

  return (
    <div style={{ height: 158, background: '#f3f4f6', borderRadius: 4, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        style={{
          width: LETTER_SIZE.w * PAGE_SCALE,
          height: LETTER_SIZE.h * PAGE_SCALE,
          background: template.globalSettings?.backgroundColor || '#fff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            width: LETTER_SIZE.w,
            height: LETTER_SIZE.h,
            transform: `scale(${PAGE_SCALE})`,
            transformOrigin: 'top left',
            position: 'relative',
            fontFamily: template.globalSettings?.fontPairing?.body || 'sans-serif'
          }}
        >
          {elements.map(renderElement)}
        </div>
      </div>
    </div>
  );
};

const TemplateGallery = ({ activeTemplateId, onSelectTemplate, keyword, defaultABT, formValues }) => {
  const templates = generateTemplates(keyword, defaultABT, formValues);

  return (
    <div style={{ width: 240, borderRight: '1px solid #e5e7eb', padding: '4px 12px', overflowY: 'auto', background: '#fff' }}>
      <Title level={5} style={{ marginBottom: 16 }}>Templates</Title>
      
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {templates.map(template => (
          <Card
            key={template.id}
            hoverable
            onClick={() => onSelectTemplate(template)}
            style={{
              borderColor: activeTemplateId === template.id ? '#38b549' : '#e5e7eb',
              borderWidth: activeTemplateId === template.id ? 2 : 1,
              padding: 0
            }}
            bodyStyle={{ padding: 8 }}
          >
            <TemplateThumbnail template={template} keyword={keyword} defaultABT={defaultABT} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: 12 }}>{template.name}</Text>
              <Button type="primary" size="small" style={{ fontSize: 11, padding: '0 6px' }} onClick={() => onSelectTemplate(template)}>Use</Button>
            </div>
          </Card>
        ))}
      </Space>
    </div>
  );
};

export default TemplateGallery;
